import React, { useMemo, useState } from "react";
import { buildFormationRows, formationLabel, summarisePlayerEvents } from "./lineupModel";
import "./football-app.css";

export const photoFor = (player) => (player?.photo ? player.photo : player?.id ? `https://media.api-sports.io/football/players/${player.id}.png` : null);

const POS = { G: "Goalkeeper", D: "Defender", M: "Midfielder", F: "Forward" };
export const positionLabel = (pos) => POS[pos] || pos || "";

export function PlayerAvatar({ player, side = "home", large = false }) {
  const [failed, setFailed] = useState(false);
  const src = photoFor(player);
  return (
    <span className={`fa-avatar ${side === "away" ? "away" : ""} ${large ? "lg" : ""}`}>
      {src && !failed ? (
        <img src={src} alt="" loading="lazy" onError={() => setFailed(true)} />
      ) : (
        <span aria-hidden="true">{player?.number ?? "·"}</span>
      )}
    </span>
  );
}

/** Compact event badges around an avatar; only recorded events are shown. */
export function EventBadges({ ev }) {
  if (!ev) return null;
  return (
    <>
      {ev.goals > 0 && <span className="fa-badge goal tr" title="Goal">⚽{ev.goals > 1 ? ev.goals : ""}</span>}
      {ev.ownGoals > 0 && <span className="fa-badge og tr" title="Own goal">OG</span>}
      {ev.assists > 0 && <span className="fa-badge assist br" title="Assist">A{ev.assists > 1 ? ev.assists : ""}</span>}
      {ev.yellow > 0 && <span className="fa-badge yellow bl" title="Yellow card" />}
      {ev.red > 0 && <span className="fa-badge red bl" title="Red card" />}
      {ev.offAt !== null && ev.offAt !== undefined && <span className="fa-badge off tl" title={`Substituted off ${ev.offAt}'`}>↓</span>}
      {ev.onAt !== null && ev.onAt !== undefined && <span className="fa-badge on tl" title={`Substituted on ${ev.onAt}'`}>↑</span>}
    </>
  );
}

const srLabel = (p, ev) => {
  const bits = [`${p.number ? `${p.number} ` : ""}${p.name}`];
  if (ev?.goals) bits.push(`${ev.goals} goal${ev.goals > 1 ? "s" : ""}`);
  if (ev?.assists) bits.push(`${ev.assists} assist${ev.assists > 1 ? "s" : ""}`);
  if (ev?.yellow) bits.push("yellow card");
  if (ev?.red) bits.push("red card");
  if (ev?.offAt != null) bits.push(`substituted off ${ev.offAt} minutes`);
  if (ev?.onAt != null) bits.push(`substituted on ${ev.onAt} minutes`);
  return `${bits.join(", ")}. Open match details`;
};

function PlayerButton({ entry, ev, side, onSelect }) {
  const p = entry.player;
  return (
    <button type="button" className="fa-player" aria-label={srLabel(p, ev)} onClick={() => onSelect(p)}>
      {ev?.offAt != null && <span className="fa-min">{ev.offAt}'</span>}
      <span className="fa-avwrap">
        <PlayerAvatar player={p} side={side} />
        <EventBadges ev={ev} />
      </span>
      <span className="fa-pname">{p.number ? `${p.number} ` : ""}{lastWord(p.name)}</span>
    </button>
  );
}
const lastWord = (name) => String(name || "").split(" ").slice(-1)[0];

function TeamLineup({ team, side, events, onSelect, active }) {
  const summary = useMemo(() => summarisePlayerEvents(team, events), [team, events]);
  const { placed, rows } = useMemo(() => buildFormationRows(team.startXI, "home"), [team.startXI]);
  // With no event feed every count would read 0, which is "unknown", not "none".
  const select = (player) => onSelect({ player, team, side, ev: events.length ? summary[player.id] : null });
  const formation = formationLabel(team);
  return (
    <section className="fa-lineup-team" data-active={active} aria-label={`${team.team?.name} lineup`}>
      <div className="fa-lineup-head">
        {team.team?.logo && <img src={team.team.logo} alt="" />}
        <span>{team.team?.name}</span>
        <small>{formation || "Formation not recorded"}</small>
      </div>
      {placed ? (
        <div className="fa-pitch" style={{ gridTemplateRows: `repeat(${rows.length}, 1fr)` }} role="group" aria-label={`${team.team?.name} starting XI${formation ? `, ${formation}` : ""}`}>
          <span className="fa-pitch-box top" aria-hidden="true" />
          <span className="fa-pitch-box bottom" aria-hidden="true" />
          {rows.map((row, i) => (
            <div className="fa-prow" key={i}>
              {row.map((e) => (
                <PlayerButton key={e.player.id ?? e.player.name} entry={e} ev={summary[e.player.id]} side={side} onSelect={select} />
              ))}
            </div>
          ))}
        </div>
      ) : (
        <>
          <p className="fa-note">Recorded pitch positions are not available for this lineup, so players are listed instead of placed.</p>
          <ul className="fa-list">
            {team.startXI.map((e) => (
              <li key={e.player.id ?? e.player.name}>
                <button type="button" onClick={() => select(e.player)}>
                  <span className="n">{e.player.number ?? "·"}</span>
                  <span>{e.player.name}</span>
                  <span className="meta">{positionLabel(e.player.pos)}</span>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
      {team.substitutes?.length > 0 && (
        <div className="fa-subs">
          <h3>Substitutes</h3>
          <ul className="fa-list">
            {team.substitutes.map((e) => {
              const ev = summary[e.player.id];
              return (
                <li key={e.player.id ?? e.player.name}>
                  <button type="button" aria-label={srLabel(e.player, ev)} onClick={() => select(e.player)}>
                    <span className="n">{e.player.number ?? "·"}</span>
                    <span>{e.player.name}</span>
                    <span className="meta">
                      {ev?.onAt != null && <span>↑ {ev.onAt}'</span>}
                      {ev?.goals > 0 && <span>⚽{ev.goals}</span>}
                      {ev?.yellow > 0 && <span>🟨</span>}
                      {ev?.red > 0 && <span>🟥</span>}
                      {positionLabel(e.player.pos)}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
      {team.coach?.name && <p className="fa-note">Coach: {team.coach.name}</p>}
    </section>
  );
}

/**
 * Lineups from recorded data. On phones one team is shown with a switcher; at
 * 900px and above both pitches sit side by side.
 */
export default function LineupPitch({ lineups, events = [], onSelectPlayer, predicted = false }) {
  const [active, setActive] = useState(0);
  if (!lineups?.length) return <div className="fa-empty">Confirmed lineups are not available yet.</div>;
  return (
    <div>
      {predicted && <p className="fa-note">Predicted lineups: the previous XI adjusted for the current injury list. Positions and numbers are not confirmed.</p>}
      {lineups.length > 1 && (
        <div className="fa-lineup-switch" role="group" aria-label="Choose team">
          {lineups.map((t, i) => (
            <button key={i} type="button" aria-pressed={active === i} onClick={() => setActive(i)}>
              {t.team?.logo && <img src={t.team.logo} alt="" />}
              <span>{t.team?.name}</span>
            </button>
          ))}
        </div>
      )}
      <div className={`fa-lineups ${lineups.length > 1 ? "both" : ""}`} style={{ marginTop: 12 }}>
        {lineups.map((team, i) => (
          <TeamLineup key={team.team?.id ?? i} team={team} side={i === 0 ? "home" : "away"} events={events} active={active === i} onSelect={onSelectPlayer} />
        ))}
      </div>
    </div>
  );
}
